import Image from "next/image";

import { Text } from "@/components/atoms/Text";
import ChevronRight24 from "@/icons/__generated__/ChevronRight/ChevronRight24";
import { cn } from "@/lib/utils";
import { cloudinaryImageLoader } from "~/utils/image-loaders";

export type FsvCardProps = {
  title: string;
  description: React.ReactNode;
  /** Shown next to the chevron on clickable cards, e.g. "Menu". */
  ctaLabel?: string | React.ReactElement;
  onClick?: () => void;
  image?: {
    externalId: string;
    alt: string;
  };
  testId: string;
};

export const FsvCard = ({
  title,
  description,
  ctaLabel,
  image,
  onClick,
  testId,
}: FsvCardProps) => {
  return (
    <div
      className={cn(
        "tw-relative tw-rounded-8 tw-shadow-sm",
        onClick &&
          "tw-transition-shadow tw-duration-500 tw-ease-out hover:tw-shadow-xl",
      )}
      data-testid={testId}
    >
      {/* button element is now a child of the card div to avoid layout shift on pageload. The card contains
      a link element, and when a clickable element is nested within a button, the elements breakout of the parent btn
      on pageload. */}
      {onClick && (
        <button
          type="button"
          onClick={onClick}
          aria-label={title}
          data-testid={`${testId}-button`}
          className="tw-absolute tw-inset-0 tw-rounded-8 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-blue-800"
        />
      )}
      {image?.externalId && (
        <div className="tw-aspect-[356/200] tw-relative tw-pointer-events-none">
          <div className="tw-block tw-w-full tw-h-full">
            <Image
              src={image.externalId}
              alt={image.alt}
              sizes="500px"
              fill={true}
              loader={cloudinaryImageLoader}
              className="tw-object-cover tw-object-center tw-rounded-t-8"
            />
          </div>
        </div>
      )}
      <div className="tw-flex tw-justify-between">
        <div className="tw-pt-12 tw-pb-16 tw-px-16 tw-flex tw-flex-col tw-flex-1 tw-min-w-0 tw-text-left md:tw-gap-2">
          <Text
            element="div"
            className="tw-text-purple-800 md:tw-truncate tw-headline-1-m md:tw-body-2-m"
          >
            {title}
          </Text>

          <div className="tw-flex tw-items-center tw-gap-4 tw-justify-between">
            <Text
              element="div"
              className="tw-text-gray-600 tw-body-2 md:tw-body-1"
            >
              {description}
            </Text>
          </div>
        </div>
        {onClick && (
          <Text
            element="span"
            typeface="body-2-m"
            className="tw-flex tw-items-center tw-shrink-0 tw-mr-12 tw-text-gray-900"
          >
            {ctaLabel && <>{ctaLabel} </>}
            <ChevronRight24 />
          </Text>
        )}
      </div>
    </div>
  );
};
